import { Injectable, NgZone } from '@angular/core'
import { Subject, BehaviorSubject } from 'rxjs'
import { AudioDevicesService } from './audio-devices.service'

export interface TranscriptResult {
  transcript: string
  confidence: number
  isFinal: boolean
  timestamp: Date
  audioData?: Blob
}

export interface SpeechRecognitionStatus {
  isListening: boolean
  isProcessing: boolean
  error?: string
}

interface ISpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number

  start(): void
  stop(): void
  abort(): void

  onstart: ((this: ISpeechRecognition, ev: Event) => void) | null
  onend: ((this: ISpeechRecognition, ev: Event) => void) | null
  onerror:
    | ((this: ISpeechRecognition, ev: SpeechRecognitionErrorEvent) => void)
    | null
  onresult:
    | ((this: ISpeechRecognition, ev: SpeechRecognitionEvent) => void)
    | null
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

export interface SpeechRecognitionResult {
  length: number
  isFinal: boolean
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

declare global {
  interface Window {
    SpeechRecognition: new () => ISpeechRecognition
    webkitSpeechRecognition: new () => ISpeechRecognition
  }
}

@Injectable({
  providedIn: 'root',
})
export class SpeechRecognitionService {
  private recognition: ISpeechRecognition | null = null
  private isSupported = false

  // テキストを蓄積
  private accumulatedTranscripts: string[] = []

  // 音声チャンクをBlobとして蓄積
  private accumulatedAudioBlobs: Blob[] = []

  private processedResultIndices: Set<number> = new Set()

  private mediaRecorder: MediaRecorder | null = null
  private selectedMimeType: string = 'audio/webm'
  private currentAudioChunks: Blob[] = []
  private currentStream: MediaStream | null = null

  // 自動再起動管理
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private autoRestartTimer: any = null
  private readonly RESTART_INTERVAL = 50000 // 50秒ごとに再起動

  // 無音検出
  private lastSpeechTime = 0
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private silenceCheckInterval: any = null
  private readonly SILENCE_DURATION = 2000 // 2秒無音で発話終了
  private readonly SILENCE_CHECK_INTERVAL = 500 // チェック頻度を500msに調整
  private isSpeaking = false

  // ノイズ検出
  private lastTextUpdateTime = 0
  private lastAccumulatedLength = 0
  private readonly NO_NEW_TEXT_TIMEOUT_MS = 10000 // 発話検出中に10秒間テキストが増えなければノイズと判断

  // Android対応: 結果タイムアウト
  private lastResultTime = 0
  private readonly NO_RESULT_TIMEOUT = 15000 // 15秒間結果が来なければ音声データで送信

  // メモリ管理
  private readonly MAX_AUDIO_BLOBS = 50 // 最大50チャンク
  private readonly MAX_AUDIO_SIZE_MB = 10 // 最大10MB
  private readonly MAX_TRANSCRIPT_SEGMENTS = 100 // 最大100セグメント

  private isManuallyStopped = false
  private isManualStop = false

  private isWaitingForResponse = false
  private isRecognitionRunning = false

  private lastRestartTime = 0
  private readonly MIN_RESTART_INTERVAL = 5000
  private selectedMicId: string | null = null

  private transcriptSubject = new Subject<TranscriptResult>()
  private statusSubject = new BehaviorSubject<SpeechRecognitionStatus>({
    isListening: false,
    isProcessing: false,
  })
  private errorSubject = new Subject<string>()
  private interimTranscriptSubject = new Subject<string>()

  public transcript$ = this.transcriptSubject.asObservable()
  public status$ = this.statusSubject.asObservable()
  public error$ = this.errorSubject.asObservable()
  public interimTranscript$ = this.interimTranscriptSubject.asObservable()

  constructor(
    private zone: NgZone,
    private audioDevices: AudioDevicesService,
  ) {
    this.initializeSpeechRecognition()
  }

  private initializeSpeechRecognition(): void {
    if (typeof window === 'undefined') {
      console.warn('SpeechRecognition not available in this environment')
      return
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition

    if (SpeechRecognition) {
      this.isSupported = true
      this.recognition = new SpeechRecognition()
      this.setupRecognition()
    } else {
      console.warn('Web Speech API is not supported in this browser')
    }
  }

  // Android: start()呼び出しを制御するラッパー
  private safeStartRecognition(): boolean {
    if (!this.recognition) return false

    if (this.isRecognitionRunning) return false

    if (
      this.audioDevices.isAndroid &&
      Date.now() - this.lastRestartTime < this.MIN_RESTART_INTERVAL
    ) {
      return false
    }

    try {
      this.lastRestartTime = Date.now()
      this.recognition.start()
      return true
    } catch (error) {
      console.warn('Recognition start error:', error)
      return false
    }
  }

  private setupRecognition(): void {
    if (!this.recognition) return

    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.lang = 'ja-JP'
    this.recognition.maxAlternatives = 1

    this.recognition.onstart = () => {
      console.log('🎤 Speech recognition started')
      this.isRecognitionRunning = true
      this.isManuallyStopped = false
      this.isSpeaking = false
      this.lastSpeechTime = Date.now()
      this.lastResultTime = Date.now()

      this.processedResultIndices.clear()

      // 状態更新のみzone.run
      this.zone.run(() => {
        this.statusSubject.next({
          isListening: true,
          isProcessing: false,
        })
      })

      // タイマー設定はzone.run不要
      // Android: 自動再起動を無効化（マイク許可音対策）
      if (!this.audioDevices.isAndroid) {
        this.setAutoRestartTimer()
      }
      this.startSilenceCheck()
    }

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      // zone.run不要 - データ処理のみ
      const results = event.results

      // 結果受け取りでタイムアウトリセット
      this.lastResultTime = Date.now()

      // interim resultsで発話検出
      for (let i = event.resultIndex; i < results.length; i++) {
        const result = results[i]

        // interim resultsで発話検出
        if (!result.isFinal && result[0].transcript.trim().length > 0) {
          this.isSpeaking = true
          this.lastSpeechTime = Date.now()
        }

        if (result.isFinal && !this.processedResultIndices.has(i)) {
          this.processedResultIndices.add(i)

          const transcript = result[0].transcript.trim()

          console.log('📝 Final transcript segment:', transcript)

          if (transcript) {
            // 途中経過の文字起こしとして通知
            this.interimTranscriptSubject.next(transcript)

            // テキストを蓄積
            this.accumulatedTranscripts.push(transcript)
            console.log(
              `📚 Accumulated ${this.accumulatedTranscripts.length} segments, total: ${this.accumulatedTranscripts.join('').length} chars`,
            )

            this.isSpeaking = false
            this.lastSpeechTime = Date.now()

            // セグメント数の上限チェック
            if (
              this.accumulatedTranscripts.length > this.MAX_TRANSCRIPT_SEGMENTS
            ) {
              console.warn(
                `⚠️ Transcript segment limit reached, sending result`,
              )
              this.sendAccumulatedResult()
            }
          }
        }
      }
    }

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('❌ Speech recognition error:', event.error)

      if (event.error === 'no-speech') {
        setTimeout(() => {
          if (this.statusSubject.value.isListening && !this.isManuallyStopped) {
            this.restart()
          }
        }, 100)
        return
      }

      if (event.error === 'aborted') {
        return
      }

      // 状態更新のみzone.run
      this.zone.run(() => {
        let errorMessage = '音声認識エラーが発生しました'

        if (
          event.error === 'not-allowed' ||
          event.error === 'service-not-allowed'
        ) {
          errorMessage =
            'マイクへのアクセスが拒否されました。ブラウザの設定でマイクの使用を許可してください。'
          this.isManualStop = true
        } else if (event.error === 'network') {
          errorMessage = 'ネットワークエラーが発生しました'
        }

        this.statusSubject.next({
          isListening: false,
          isProcessing: false,
          error: errorMessage,
        })

        // エラーを通知
        this.errorSubject.next(errorMessage)
      })
    }

    this.recognition.onend = () => {
      console.log('🔇 Speech recognition ended')
      this.isRecognitionRunning = false
      this.clearAutoRestartTimer()

      const currentStatus = this.statusSubject.value

      if (!this.isManuallyStopped && !this.isManualStop) {
        if (currentStatus.isProcessing || currentStatus.isListening) {
          setTimeout(() => {
            if (!this.isManuallyStopped && !this.isManualStop) {
              this.forceStartRecognition()
            }
          }, 100)
        }
      } else if (!currentStatus.isProcessing) {
        this.zone.run(() => {
          this.statusSubject.next({
            isListening: false,
            isProcessing: false,
          })
        })
      }
    }
  }

  // 50秒ごとに自動再起動
  private setAutoRestartTimer(): void {
    this.clearAutoRestartTimer()

    this.autoRestartTimer = setTimeout(async () => {
      if (
        this.recognition &&
        this.statusSubject.value.isListening &&
        !this.isManualStop
      ) {
        console.log(
          `⏱️ Auto-restarting at 50s (accumulated: ${this.accumulatedTranscripts.length} segments, ${this.accumulatedAudioBlobs.length} audio blobs)`,
        )

        // 音声録音を一時停止して保存（Blobとして）
        await this.saveCurrentAudioChunk()

        await this.startMediaRecorder()

        this.restartForContinuousListening()
      }
    }, this.RESTART_INTERVAL)
  }

  private clearAutoRestartTimer(): void {
    if (this.autoRestartTimer) {
      clearTimeout(this.autoRestartTimer)
      this.autoRestartTimer = null
    }
  }

  private async restartForContinuousListening(): Promise<void> {
    try {
      this.clearAutoRestartTimer()
      if (this.recognition) {
        this.recognition.stop()

        setTimeout(() => {
          if (!this.isManualStop && this.statusSubject.value.isListening) {
            this.restart()
          }
        }, 200)
      }
    } catch (error) {
      console.error('Failed to restart recognition:', error)
    }
  }

  // 無音検出
  private startSilenceCheck(): void {
    this.stopSilenceCheck()

    this.silenceCheckInterval = setInterval(() => {
      const currentStatus = this.statusSubject.value
      if (currentStatus.isProcessing) {
        return
      }

      // AI応答待ち中は送信しない
      if (this.isWaitingForResponse) {
        return
      }

      // ノイズ検出: 発話検出中にテキストが増えているかチェック
      const currentLength = this.accumulatedTranscripts.join('').length
      if (currentLength > this.lastAccumulatedLength) {
        this.lastTextUpdateTime = Date.now()
        this.lastAccumulatedLength = currentLength
      }

      // 発話検出中 + テキストが長時間増えていない = ノイズと判断
      if (this.lastTextUpdateTime > 0) {
        const timeSinceLastTextUpdate = Date.now() - this.lastTextUpdateTime
        if (timeSinceLastTextUpdate > this.NO_NEW_TEXT_TIMEOUT_MS) {
          this.isSpeaking = false
          this.lastTextUpdateTime = Date.now()
          this.lastAccumulatedLength = 0
          this.lastSpeechTime = Date.now()
          return
        }
      }

      // 状態チェックを効率化
      if (this.accumulatedTranscripts.length > 0) {
        const timeSinceLastTextUpdate = Date.now() - this.lastTextUpdateTime
        if (timeSinceLastTextUpdate >= this.SILENCE_DURATION) {
          this.sendAccumulatedResult()
        }
      }

      // Android対応: テキストがなくても音声データがあり、長時間結果が来ない場合は送信
      const hasAudioData =
        this.accumulatedAudioBlobs.length > 0 ||
        this.currentAudioChunks.length > 0
      if (this.accumulatedTranscripts.length === 0 && hasAudioData) {
        const timeSinceLastResult = Date.now() - this.lastResultTime
        if (timeSinceLastResult >= this.NO_RESULT_TIMEOUT) {
          console.log(
            `⚠️ No transcript for ${timeSinceLastResult}ms but have audio, sending audio-only`,
          )
          this.sendAccumulatedResultWithAudioOnly()
          this.lastResultTime = Date.now()
        }
      }
    }, this.SILENCE_CHECK_INTERVAL)
  }

  private stopSilenceCheck(): void {
    if (this.silenceCheckInterval) {
      clearInterval(this.silenceCheckInterval)
      this.silenceCheckInterval = null
    }
  }

  // Android対応: 音声データのみでも送信
  private async sendAccumulatedResultWithAudioOnly(): Promise<void> {
    await this.saveCurrentAudioChunk()

    if (this.accumulatedAudioBlobs.length === 0) {
      return
    }

    const fullAudioData = new Blob(this.accumulatedAudioBlobs, {
      type: this.selectedMimeType,
    })

    this.zone.run(() => {
      this.transcriptSubject.next({
        transcript: '[音声データのみ]',
        confidence: 0.5,
        isFinal: true,
        timestamp: new Date(),
        audioData: fullAudioData,
      })

      this.statusSubject.next({
        isListening: false,
        isProcessing: true,
      })
    })

    this.accumulatedAudioBlobs = []
  }
  // 蓄積をクリア
  private clearAccumulations(): void {
    this.accumulatedTranscripts = []
    this.accumulatedAudioBlobs = []
    this.processedResultIndices.clear()
    this.lastTextUpdateTime = 0
    this.lastAccumulatedLength = 0
  }

  // 蓄積した結果を送信（Blob形式）
  private async sendAccumulatedResult(): Promise<void> {
    if (this.accumulatedTranscripts.length === 0) return

    // 最後の音声チャンクも保存
    await this.saveCurrentAudioChunk()

    // テキストを結合
    const fullTranscript = this.accumulatedTranscripts.join('')
    console.log(
      `📤 Sending final result: ${this.accumulatedTranscripts.length} segments, ${fullTranscript.length} chars`,
    )
    console.log(`   Audio blobs: ${this.accumulatedAudioBlobs.length} chunks`)

    // 音声Blobをまとめる（Base64変換なし）
    let fullAudioData: Blob | undefined = undefined
    if (this.accumulatedAudioBlobs.length > 0) {
      fullAudioData = new Blob(this.accumulatedAudioBlobs, {
        type: this.selectedMimeType,
      })
      console.log(
        `   Combined audio size: ${fullAudioData.size} bytes (${(fullAudioData.size / 1024 / 1024).toFixed(2)} MB)`,
      )
    }

    // zone.runを最小限に - 結果送信と状態更新のみ
    this.zone.run(() => {
      this.transcriptSubject.next({
        transcript: fullTranscript,
        confidence: 0.9,
        isFinal: true,
        timestamp: new Date(),
        audioData: fullAudioData,
      })

      this.statusSubject.next({
        isListening: false,
        isProcessing: true,
      })
    })

    // 蓄積をクリア
    this.clearAccumulations()
  }

  // 現在の音声チャンクを保存（Blobとして）
  private async saveCurrentAudioChunk(): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      const audioBlob = await this.stopMediaRecorder()
      if (audioBlob) {
        this.accumulatedAudioBlobs.push(audioBlob)

        // メモリ上限チェック（個数とサイズ）
        const totalSize = this.accumulatedAudioBlobs.reduce(
          (sum, blob) => sum + blob.size,
          0,
        )
        const totalSizeMB = totalSize / (1024 * 1024)

        if (
          this.accumulatedAudioBlobs.length > this.MAX_AUDIO_BLOBS ||
          totalSizeMB > this.MAX_AUDIO_SIZE_MB
        ) {
          console.warn(
            `⚠️ Audio limit reached (${this.accumulatedAudioBlobs.length} blobs, ${totalSizeMB.toFixed(2)}MB), auto-sending`,
          )

          // 自動送信してメモリを解放
          await this.sendAccumulatedResult()
          return
        }

        console.log(
          `💾 Saved audio blob #${this.accumulatedAudioBlobs.length} (${audioBlob.size} bytes, total: ${totalSizeMB.toFixed(2)}MB)`,
        )
      }
    }
  }

  private async startMediaRecorder(): Promise<void> {
    try {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        return
      }

      this.currentAudioChunks = []

      if (this.currentStream) {
        this.currentStream.getTracks().forEach((track) => track.stop())
        this.currentStream = null
      }

      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
        sampleRate: 16000, // 音質と容量のバランス
      }
      if (this.selectedMicId) {
        audioConstraints.deviceId = { exact: this.selectedMicId }
      }

      this.currentStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      })

      // 対応コーデックを確認
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4', // iOS Safari/Chrome用
        'audio/mp4;codecs=aac', // iOS Safari/Chrome用
        'audio/ogg;codecs=opus',
      ]

      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          this.selectedMimeType = mimeType
          console.log(`✅ Selected mime type: ${mimeType}`)
          break
        }
      }

      this.mediaRecorder = new MediaRecorder(this.currentStream, {
        mimeType: this.selectedMimeType,
        audioBitsPerSecond: 24000, // 品質と容量のバランス
      })

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.currentAudioChunks.push(event.data)
        }
      }

      // 1秒ごとにチャンク生成（オーバーヘッド削減）
      this.mediaRecorder.start(1000)
    } catch (error) {
      console.error('Failed to start media recorder:', error)
    }
  }

  // MediaRecorderをBlobとして返す
  private async stopMediaRecorder(): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        resolve(null)
        return
      }

      this.mediaRecorder.onstop = () => {
        if (this.currentAudioChunks.length === 0) {
          resolve(null)
          return
        }

        const audioBlob = new Blob(this.currentAudioChunks, {
          type: this.selectedMimeType,
        })
        this.currentAudioChunks = []
        resolve(audioBlob)
      }

      this.mediaRecorder.stop()
    })
  }

  public async start(micId?: string | null): Promise<void> {
    if (!this.isSupported) {
      console.error('Speech Recognition is not supported')
      this.statusSubject.next({
        isListening: false,
        isProcessing: false,
        error: 'お使いのブラウザは音声認識に対応していません',
      })
      return
    }

    if (this.statusSubject.value.isListening) {
      console.warn('Speech recognition is already running')
      return
    }

    try {
      this.isManuallyStopped = false
      this.isManualStop = false
      this.isSpeaking = false
      this.lastSpeechTime = Date.now()
      this.lastRestartTime = 0 // 初回起動時はリセット
      this.lastResultTime = Date.now()

      // 蓄積をクリア
      this.clearAccumulations()

      this.selectedMicId = micId || null

      await this.startMediaRecorder()
      this.safeStartRecognition()
    } catch (error) {
      console.error('Failed to start speech recognition:', error)
      this.statusSubject.next({
        isListening: false,
        isProcessing: false,
        error: '音声認識の開始に失敗しました',
      })
    }
  }

  public stop(): void {
    if (!this.recognition) return

    try {
      this.isManuallyStopped = true
      this.isManualStop = true
      this.isSpeaking = false

      this.clearAutoRestartTimer()
      this.stopSilenceCheck()

      this.recognition.stop()

      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
      }

      if (this.currentStream) {
        this.currentStream.getTracks().forEach((track) => track.stop())
        this.currentStream = null
      }

      // 蓄積をクリア
      this.clearAccumulations()

      this.statusSubject.next({
        isListening: false,
        isProcessing: false,
      })
    } catch (error) {
      console.error('Failed to stop speech recognition:', error)
    }
  }

  private restart(): void {
    if (
      !this.recognition ||
      !this.statusSubject.value.isListening ||
      this.isManualStop
    )
      return

    this.safeStartRecognition()
  }

  private forceStartRecognition(): void {
    if (!this.recognition || this.isManualStop || this.isManuallyStopped) {
      return
    }

    if (this.isRecognitionRunning) {
      return
    }

    try {
      this.safeStartRecognition()
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error)
      console.warn('Recognition start error (may be already running):', error)
      this.errorSubject.next(`Recognition error: ${errorMsg}`)
    }
  }

  public async onProcessingComplete(): Promise<void> {
    console.log('onProcessingComplete called', {
      isProcessing: this.statusSubject.value.isProcessing,
      isRecognitionRunning: this.isRecognitionRunning,
    })

    if (this.isManuallyStopped || this.isManualStop) {
      return
    }

    await this.startMediaRecorder()

    this.isSpeaking = false
    this.lastSpeechTime = Date.now()
    this.lastResultTime = Date.now()

    // 状態更新
    this.zone.run(() => {
      this.statusSubject.next({
        isListening: true,
        isProcessing: false,
      })
    })

    // Speech Recognitionが停止していたら再開
    if (!this.isRecognitionRunning) {
      this.forceStartRecognition()
    }
  }

  public isAvailable(): boolean {
    return this.isSupported
  }

  public setWaitingForResponse(waiting: boolean): void {
    this.isWaitingForResponse = waiting

    if (!waiting) {
      this.clearAccumulations()
      this.lastSpeechTime = Date.now()
    }
  }
}
